import React, { useState } from "react";
import {
  Container, Card, Typography, TextField, Button, Box, CircularProgress,
  Alert, Link, Divider
} from "@mui/material";
import { signIn, confirmSignIn, SignInInput } from 'aws-amplify/auth';
import { useNavigate } from "react-router-dom";

const SignIn = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Force change password state
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const input: SignInInput = {
        username: formData.email,
        password: formData.password,
      };
      const result = await signIn(input);

      // Check if user needs to set a new password (admin-created user first login)
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setLoading(false);
        return;
      }

      // Successful sign in
      navigate("/");
      window.location.reload();
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }

    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });

      if (result.isSignedIn) {
        navigate("/");
        window.location.reload();
      } else {
        setError("Failed to set new password. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to set new password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Force Change Password UI
  if (needsNewPassword) {
    return (
      <Container maxWidth="xs" sx={{ mt: 6 }}>
        <Card sx={{ p: 4, boxShadow: 3, borderRadius: 3 }}>
          <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
            Set New Password
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Your account requires a new password. Please set a permanent password below.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSetNewPassword}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              helperText="Minimum 8 characters with uppercase, lowercase, number, and symbol"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ py: 1.5 }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : "Set Password"}
            </Button>
          </Box>
        </Card>
      </Container>
    );
  }

  // Normal Sign In UI
  return (
    <Container maxWidth="xs" sx={{ mt: 6 }}>
      <Card sx={{ p: 4, boxShadow: 3, borderRadius: 3 }}>
        <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
          Sign In
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            sx={{ mb: 1 }}
          />

          {/* Forgot Password Link */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <Link component="button" variant="body2" onClick={() => navigate("/forgot-password")}>
              Forgot Password?
            </Link>
          </Box>

          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ py: 1.5 }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
          </Button>
        </Box>

      </Card>
    </Container>
  );
};

export default SignIn;
